class SimpleProductGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;

    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-simple-gallery-thumb]');
      if (thumb) {
        this.show(parseInt(thumb.dataset.simpleGalleryThumb, 10));
        return;
      }

      if (event.target.closest('[data-simple-gallery-prev]')) {
        this.show(this.currentIndex - 1);
        return;
      }

      if (event.target.closest('[data-simple-gallery-next]')) {
        this.show(this.currentIndex + 1);
      }
    });

    // Swipe to change slide. Bound on `this` (not on the inner .simple-gallery-main child),
    // because connectedCallback can fire before that child is parsed - delegation avoids
    // depending on load order, same as the click handler above.
    let startX = 0;
    let startY = 0;
    let dragging = false;
    let axis = null; // 'x' or 'y', decided once we know the swipe direction
    const threshold = 40; // px of horizontal movement needed to change slide

    this.addEventListener(
      'touchstart',
      (event) => {
        if (!event.target.closest('[data-simple-gallery-main]') || this.slides.length < 2) {
          dragging = false;
          return;
        }
        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        dragging = true;
        axis = null;
      },
      { passive: true }
    );

    this.addEventListener(
      'touchmove',
      (event) => {
        if (!dragging) return;
        const touch = event.touches[0];
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;

        if (axis === null && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
          axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        // Once it's clearly a horizontal swipe, stop the page from scrolling vertically.
        if (axis === 'x' && event.cancelable) event.preventDefault();
      },
      { passive: false }
    );

    this.addEventListener(
      'touchend',
      (event) => {
        if (!dragging) return;
        dragging = false;
        if (axis !== 'x') return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - startX;
        if (Math.abs(dx) < threshold) return;
        this.show(this.currentIndex + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );
  }

  connectedCallback() {
    const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    this.currentIndex = Math.max(activeIndex, 0);
  }

  get slides() {
    return Array.from(this.querySelectorAll('[data-simple-gallery-slide]'));
  }

  get thumbs() {
    return Array.from(this.querySelectorAll('[data-simple-gallery-thumb]'));
  }

  show(index) {
    const slides = this.slides;
    const thumbs = this.thumbs;
    if (!slides.length) return;
    const total = slides.length;
    const nextIndex = (index + total) % total;

    const previousSlide = slides[this.currentIndex];
    if (previousSlide && this.currentIndex !== nextIndex) this.pauseMedia(previousSlide);
    slides[this.currentIndex]?.classList.remove('is-active');
    thumbs[this.currentIndex]?.classList.remove('is-active');

    slides[nextIndex]?.classList.add('is-active');
    thumbs[nextIndex]?.classList.add('is-active');

    const activeThumb = thumbs[nextIndex];
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }

    this.currentIndex = nextIndex;
    this.playMedia(slides[nextIndex]);
  }

  // Auto-play the video on the slide the customer just picked (no need to press play).
  playMedia(slide) {
    if (!slide) return;
    const video = slide.querySelector('video');
    if (video) {
      video.playsInline = true;
      const attempt = video.play();
      if (attempt && attempt.catch) {
        attempt.catch(() => {
          // Browser blocked playback with sound – start muted instead.
          video.muted = true;
          video.play().catch(() => {});
        });
      }
      return;
    }
    const youtube = slide.querySelector('iframe.youtube-video');
    if (youtube && youtube.contentWindow) {
      youtube.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), '*');
    }
  }

  pauseMedia(slide) {
    const video = slide.querySelector('video');
    if (video && !video.paused) video.pause();
    const youtube = slide.querySelector('iframe.youtube-video');
    if (youtube && youtube.contentWindow) {
      youtube.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
    }
  }
}

if (!customElements.get('simple-product-gallery')) {
  customElements.define('simple-product-gallery', SimpleProductGallery);
}
